import React from 'react';
import { useNavigate } from 'react-router-dom';
import Dashboard from '../../pages/dashboard/Dashboard.tsx';
import { Helmet } from 'react-helmet-async';
import { Container, Typography, Sheet, Stack, Skeleton, Button, Grid, Tabs, TabList, Tab, TabPanel, Table, Chip, Box, Select, Option, Input, FormControl, FormLabel } from '@mui/joy';
import { createApiClient } from '../../api/poshwatch_client.ts';
import { ClosetMetrics, Log, ClosetMetricsSubscriptionTypeEnum, ActionStats } from 'poshwatch_ts_client';
import { FacebookPixelEventsCard } from './components/FacebookPixelEvents.tsx';

// Component for displaying users with offers
const UsersWithOffersCard = ({ count, isLoading }: { count: number; isLoading: boolean }) => (
  <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm' }}>
    <Typography level="title-lg" sx={{ mb: 2 }}>Users with Offers</Typography>
    {isLoading ? (
      <Typography><Skeleton>Loading users with offers count...</Skeleton></Typography>
    ) : (
      <Typography>{count} users have enabled offers</Typography>
    )}
  </Sheet>
);

// Component for displaying schedules by hour
const SchedulesByHourCard = ({
  schedulesByHour,
  isLoading
}: {
  schedulesByHour: Record<string, Array<{ userId: string; closetId: string }>>;
  isLoading: boolean;
}) => {
  const [showUsernames, setShowUsernames] = React.useState(false);
  const [filterText, setFilterText] = React.useState('');

  // Simplified helper function to get users for an hour
  const getUsersForHour = (hour: number): Array<{ userId: string; closetId: string }> => {
    const users = schedulesByHour[hour.toString()] || [];
    if (!filterText.trim()) return users;
    const searchText = filterText.toLowerCase().trim();
    return users.filter(user =>
      user.userId.toLowerCase() === searchText ||
      user.closetId.toLowerCase() === searchText
    );
  };

  // Helper function to convert UTC to PST
  const utcToPst = (utcHour: number): number => {
    const pstHour = (utcHour - 8 + 24) % 24;
    return pstHour;
  };

  // Helper function to format hour
  const formatHour = (hour: number): string => {
    const formattedHour = (hour % 12 || 12).toString();
    const ampm = hour < 12 ? 'AM' : 'PM';
    return `${formattedHour} ${ampm}`;
  };

  // Check if there are any users matching the filter
  const hasMatchingUsers = React.useMemo(() => {
    if (!filterText.trim()) return true;
    const searchText = filterText.toLowerCase().trim();
    return Object.values(schedulesByHour).some(users =>
      users.some(user =>
        user.userId.toLowerCase() === searchText ||
        user.closetId.toLowerCase() === searchText
      )
    );
  }, [schedulesByHour, filterText]);

  return (
    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm' }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography level="title-lg">Schedules by Hour</Typography>
        <Button
          variant="outlined"
          onClick={() => setShowUsernames(!showUsernames)}
          size="sm"
        >
          Show {showUsernames ? 'Counts' : 'Usernames'}
        </Button>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <input
          type="text"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          placeholder="Filter by exact user ID or closet ID"
          style={{
            padding: '8px 12px',
            borderRadius: '4px',
            border: '1px solid var(--joy-palette-neutral-300)',
            flex: 1
          }}
        />
      </Stack>

      {isLoading ? (
        <Grid container spacing={2} columns={4}>
          {[...Array(24)].map((_, index) => (
            <Grid xs={1} key={index}>
              <Sheet variant="soft" sx={{ p: 2, height: '100px' }}>
                <Typography><Skeleton>Loading...</Skeleton></Typography>
              </Sheet>
            </Grid>
          ))}
        </Grid>
      ) : !hasMatchingUsers ? (
        <Typography level="body-lg" sx={{ textAlign: 'center', py: 4 }}>
          No users found matching "{filterText}"
        </Typography>
      ) : (
        <Grid container spacing={2} columns={showUsernames ? 2 : 4}>
          {[...Array(24)].map((_, i) => {
            const index = (i + 8) % 24; // Start with 12 AM PST (8 UTC)
            const users = getUsersForHour(index);
            const pstHour = utcToPst(index);

            return (
              <Grid xs={1} key={index}>
                <Sheet
                  variant="soft"
                  sx={{
                    p: 2,
                    height: showUsernames ? 'auto' : '100px',
                    minHeight: '100px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1
                  }}
                >
                  <Typography level="title-sm" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>{formatHour(pstHour)}</span>
                    <span>{index}:00 UTC</span>
                  </Typography>
                  {showUsernames ? (
                    <>
                      <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                        {users.length} user{users.length !== 1 ? 's' : ''}
                      </Typography>
                      <Typography level="body-sm">
                        {users.length > 0 ? (
                          users.sort((a, b) => a.closetId.localeCompare(b.closetId)).map((user, i) => (
                            <React.Fragment key={user.userId}>
                              {user.closetId}
                              {i < users.length - 1 && <br />}
                            </React.Fragment>
                          ))
                        ) : (
                          'No users'
                        )}
                      </Typography>
                    </>
                  ) : (
                    <Typography level="h4">{users.length}</Typography>
                  )}
                </Sheet>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Sheet>
  );
};

// Time period options for the metrics filter
type TimePeriod = 'last24h' | 'last7d' | 'last30d' | 'total';
const TIME_PERIOD_LABELS: Record<TimePeriod, string> = {
  last24h: 'Past 24 Hours',
  last7d: 'Past 7 Days',
  last30d: 'Past 30 Days',
  total: 'All Time',
};

type SubscriptionFilter = 'all' | 'subscribed' | 'trial' | 'non_subscribed';
type ActivityFilter = 'all' | 'active' | 'inactive';


// New component for displaying closet metrics
const ClosetMetricsCard = ({
  metrics,
  isLoading,
  onFetchMetrics,
  hasFetched
}: {
  metrics: ClosetMetrics[];
  isLoading: boolean;
  onFetchMetrics: () => void;
  hasFetched: boolean;
}) => {
  const [timePeriod, setTimePeriod] = React.useState<TimePeriod>('last24h');
  const [subscriptionFilter, setSubscriptionFilter] = React.useState<SubscriptionFilter>('subscribed');
  const [activityFilter, setActivityFilter] = React.useState<ActivityFilter>('all');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [sortColumn, setSortColumn] = React.useState<string>('shares');
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('desc');
  const [statusFilter, setStatusFilter] = React.useState<'all' | 'healthy' | 'no_shares'>('all');
  const navigate = useNavigate();

  // Helper function to get emoji for subscription type
  const getSubscriptionEmoji = (subscriptionType: ClosetMetricsSubscriptionTypeEnum) => {
    switch (subscriptionType) {
      case ClosetMetricsSubscriptionTypeEnum.TrialMobile:
        return '🍏 🆓';
      case ClosetMetricsSubscriptionTypeEnum.TrialWeb:
        return '🌐 🆓';
      case ClosetMetricsSubscriptionTypeEnum.SubscribedMobile:
        return '🍏';
      case ClosetMetricsSubscriptionTypeEnum.SubscribedWeb:
        return '🌐 ';
      case ClosetMetricsSubscriptionTypeEnum.NonSubscribed:
        return '❌';
      default:
        return subscriptionType || '❓ Unknown';
    }
  };

  const getStat = React.useCallback((stats: ActionStats, action: keyof ActionStats): number =>
    stats[action][timePeriod], [timePeriod]);

  // Filter metrics based on all active filters
  const filteredMetrics = React.useMemo(() => {
    return metrics.filter(m => {
      const isTrial = m.subscriptionType === ClosetMetricsSubscriptionTypeEnum.TrialMobile || m.subscriptionType === ClosetMetricsSubscriptionTypeEnum.TrialWeb;
      if (subscriptionFilter === 'subscribed' && (m.subscriptionType === ClosetMetricsSubscriptionTypeEnum.NonSubscribed || isTrial)) return false;
      if (subscriptionFilter === 'trial' && !isTrial) return false;
      if (subscriptionFilter === 'non_subscribed' && m.subscriptionType !== ClosetMetricsSubscriptionTypeEnum.NonSubscribed) return false;
      if (activityFilter === 'active' && !m.isActive) return false;
      if (activityFilter === 'inactive' && m.isActive) return false;
      if (statusFilter !== 'all') {
        const isSubscribed = m.subscriptionType !== ClosetMetricsSubscriptionTypeEnum.NonSubscribed;
        const shares = getStat(m.stats, 'share');
        const isSubscribedNoShares = isSubscribed && shares === 0;
        if (statusFilter === 'healthy' && (!isSubscribed || isSubscribedNoShares)) return false;
        if (statusFilter === 'no_shares' && !isSubscribedNoShares) return false;
      }
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!m.userId.toLowerCase().includes(q) && !m.closetId.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [metrics, subscriptionFilter, activityFilter, statusFilter, searchQuery, timePeriod]);

  // Sort by selected column
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection(column === 'userId' || column === 'closetId' ? 'asc' : 'desc');
    }
  };

  const sortedMetrics = React.useMemo(() => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...filteredMetrics].sort((a, b) => {
      switch (sortColumn) {
        case 'userId': return dir * a.userId.localeCompare(b.userId);
        case 'closetId': return dir * a.closetId.localeCompare(b.closetId);
        case 'subType': return dir * (a.subscriptionType || '').localeCompare(b.subscriptionType || '');
        case 'linked': return dir * ((a.isAccountLinked ? 1 : 0) - (b.isAccountLinked ? 1 : 0));
        case 'shares': return dir * (getStat(a.stats, 'share') - getStat(b.stats, 'share'));
        case 'cShares': return dir * (getStat(a.stats, 'community_share') - getStat(b.stats, 'community_share'));
        case 'offers': return dir * (getStat(a.stats, 'offer_to_likers') - getStat(b.stats, 'offer_to_likers'));
        case 'relistings': return dir * (getStat(a.stats, 'relisting') - getStat(b.stats, 'relisting'));
        case 'active': return dir * ((a.isActive ? 1 : 0) - (b.isActive ? 1 : 0));
        default: return 0;
      }
    });
  }, [filteredMetrics, sortColumn, sortDirection, getStat]);

  // Summary statistics (computed from filtered data)
  const totalClosets = filteredMetrics.length;
  const activeClosets = filteredMetrics.filter(m => m.isActive).length;
  const inactiveClosets = totalClosets - activeClosets;
  const subscribedClosets = filteredMetrics.filter(m => m.subscriptionType !== ClosetMetricsSubscriptionTypeEnum.NonSubscribed).length;
  const unsubscribedClosets = totalClosets - subscribedClosets;
  const avgShares = totalClosets > 0 ? filteredMetrics.reduce((sum, m) => sum + getStat(m.stats, 'share'), 0) / totalClosets : 0;
  const totalCommunityShares = filteredMetrics.reduce((sum, m) => sum + getStat(m.stats, 'community_share'), 0);
  const totalOffers = filteredMetrics.reduce((sum, m) => sum + getStat(m.stats, 'offer_to_likers'), 0);
  const totalRelistings = filteredMetrics.reduce((sum, m) => sum + getStat(m.stats, 'relisting'), 0);

  const handleMetricSelect = (metric: ClosetMetrics) => {
    navigate(`/admin/${metric.userId}/${metric.closetId}`);
  };

  return (
    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm' }}>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography level="title-lg">Closet Metrics</Typography>
        <Button
          variant="solid"
          onClick={onFetchMetrics}
          loading={isLoading}
          disabled={isLoading}
        >
          {hasFetched ? 'Refresh Metrics' : 'Fetch Metrics'}
        </Button>
      </Stack>

      {!hasFetched && !isLoading ? (
        <Typography level="body-lg" sx={{ textAlign: 'center', py: 4 }}>
          Click "Fetch Metrics" to load closet metrics data
        </Typography>
      ) : isLoading ? (
        <Stack spacing={2}>
          {[...Array(5)].map((_, i) => (
            <Typography key={i}><Skeleton>Loading metrics...</Skeleton></Typography>
          ))}
        </Stack>
      ) : (
        <>
          {/* Filter bar */}
          <Sheet variant="soft" sx={{ p: 2, mb: 2, borderRadius: 'sm' }}>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <FormControl size="sm">
                <FormLabel>Time Period</FormLabel>
                <Select
                  value={timePeriod}
                  onChange={(_e, val) => val && setTimePeriod(val)}
                  sx={{ minWidth: 140 }}
                >
                  {(Object.keys(TIME_PERIOD_LABELS) as TimePeriod[]).map(key => (
                    <Option key={key} value={key}>{TIME_PERIOD_LABELS[key]}</Option>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="sm">
                <FormLabel>Subscription</FormLabel>
                <Select
                  value={subscriptionFilter}
                  onChange={(_e, val) => val && setSubscriptionFilter(val)}
                  sx={{ minWidth: 140 }}
                >
                  <Option value="all">All</Option>
                  <Option value="subscribed">Subscribed</Option>
                  <Option value="trial">Trial</Option>
                  <Option value="non_subscribed">Not Subscribed</Option>
                </Select>
              </FormControl>
              <FormControl size="sm">
                <FormLabel>Activity</FormLabel>
                <Select
                  value={activityFilter}
                  onChange={(_e, val) => val && setActivityFilter(val)}
                  sx={{ minWidth: 120 }}
                >
                  <Option value="all">All</Option>
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                </Select>
              </FormControl>
              <FormControl size="sm">
                <FormLabel>Status</FormLabel>
                <Select
                  value={statusFilter}
                  onChange={(_e, val) => val && setStatusFilter(val)}
                  sx={{ minWidth: 140 }}
                >
                  <Option value="all">All</Option>
                  <Option value="healthy">Healthy (Green)</Option>
                  <Option value="no_shares">No Shares (Red)</Option>
                </Select>
              </FormControl>
              <FormControl size="sm">
                <FormLabel>Search</FormLabel>
                <Input
                  placeholder="User ID or Closet ID"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  sx={{ minWidth: 180 }}
                />
              </FormControl>
            </Stack>
          </Sheet>

          {/* Summary stats */}
          <Sheet variant="soft" sx={{ p: 2, mb: 2 }}>
            <Typography level="title-sm" sx={{ mb: 1 }}>{TIME_PERIOD_LABELS[timePeriod]}</Typography>
            <Grid container spacing={2} columns={3}>
              <Grid xs={1}>
                <Typography level="title-sm">Total Closets</Typography>
                <Typography level="h4">{totalClosets}</Typography>
              </Grid>
              <Grid xs={1}>
                <Typography level="title-sm">Active</Typography>
                <Typography level="h4">{activeClosets}</Typography>
              </Grid>
              <Grid xs={1}>
                <Typography level="title-sm">Inactive</Typography>
                <Typography level="h4">{inactiveClosets}</Typography>
              </Grid>
              <Grid xs={1}>
                <Typography level="title-sm">Subscribed</Typography>
                <Typography level="h4">{subscribedClosets}</Typography>
              </Grid>
              <Grid xs={1}>
                <Typography level="title-sm">Unsubscribed</Typography>
                <Typography level="h4">{unsubscribedClosets}</Typography>
              </Grid>
              <Grid xs={1}>
                <Typography level="title-sm">Avg Shares</Typography>
                <Typography level="h4">{avgShares.toFixed(1)}</Typography>
              </Grid>
              <Grid xs={1}>
                <Typography level="title-sm">Community Shares</Typography>
                <Typography level="h4">{totalCommunityShares.toLocaleString()}</Typography>
              </Grid>
              <Grid xs={1}>
                <Typography level="title-sm">Offers</Typography>
                <Typography level="h4">{totalOffers.toLocaleString()}</Typography>
              </Grid>
              <Grid xs={1}>
                <Typography level="title-sm">Relistings</Typography>
                <Typography level="h4">{totalRelistings.toLocaleString()}</Typography>
              </Grid>
            </Grid>
          </Sheet>

          {/* Data table */}
          <Sheet variant="soft" sx={{ p: 2 }}>
            <Sheet
              sx={{
                overflowX: 'auto',
              }}
            >
              <Table sx={{ minWidth: 900, tableLayout: 'auto' }}>
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    {[
                      { key: 'userId', label: 'User ID' },
                      { key: 'closetId', label: 'Closet ID' },
                      { key: 'subType', label: 'Sub Type' },
                      { key: 'linked', label: 'Linked' },
                      { key: 'shares', label: 'Shares' },
                      { key: 'cShares', label: 'C. Shares' },
                      { key: 'offers', label: 'Offers' },
                      { key: 'relistings', label: 'Relistings' },
                      { key: 'active', label: 'Active' },
                    ].map(col => (
                      <th
                        key={col.key}
                        onClick={() => handleSort(col.key)}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        {col.label} {sortColumn === col.key ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedMetrics.map((metric, index) => {
                    const isSubscribed = metric.subscriptionType !== ClosetMetricsSubscriptionTypeEnum.NonSubscribed;
                    const shares = getStat(metric.stats, 'share');
                    const isSubscribedNoShares = isSubscribed && shares === 0;

                    return (
                      <tr
                        key={`${metric.userId}::${metric.closetId}`}
                        onClick={() => handleMetricSelect(metric)}
                        style={{
                          cursor: 'pointer',
                          backgroundColor: isSubscribedNoShares
                            ? 'var(--joy-palette-danger-softBg)'
                            : isSubscribed
                              ? 'var(--joy-palette-success-softBg)'
                              : 'inherit'
                        }}
                      >
                        <td>{index + 1}</td>
                        <td style={{ whiteSpace: 'nowrap' }} title={metric.userId}>
                          {metric.userId}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>{metric.closetId}</td>
                        <td>{getSubscriptionEmoji(metric.subscriptionType)}</td>
                        <td>{metric.isAccountLinked === null ? '❓' : (metric.isAccountLinked ? '✅' : '❌')}</td>
                        <td style={{
                          color: isSubscribedNoShares ? 'var(--joy-palette-danger-500)' : 'inherit',
                          fontWeight: isSubscribedNoShares ? 'bold' : 'normal'
                        }}>
                          {shares.toLocaleString()}
                        </td>
                        <td>{getStat(metric.stats, 'community_share').toLocaleString()}</td>
                        <td>{getStat(metric.stats, 'offer_to_likers').toLocaleString()}</td>
                        <td>{getStat(metric.stats, 'relisting').toLocaleString()}</td>
                        <td>{metric.isActive ? 'Yes' : 'No'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Sheet>
          </Sheet>
        </>
      )}
    </Sheet>
  );
};

// Constants and types
const LOGS_PER_PAGE = 25;
const LOG_TYPES = ['error', 'task_complete', 'action', 'info', 'warning'] as const;

// Helper functions
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString();
};

const getLogTypeColor = (type: string) => {
  switch (type) {
    case 'error':
      return 'danger';
    case 'task_complete':
      return 'success';
    case 'action':
      return 'primary';
    case 'warning':
      return 'warning';
    default:
      return 'neutral';
  }
};

// User Search Form Component
const UserSearchForm = ({ 
  username, 
  setUsername, 
  onSearch, 
  isLoading, 
  error 
}: {
  username: string;
  setUsername: (value: string) => void;
  onSearch: () => void;
  isLoading: boolean;
  error: string | null;
}) => (
  <Stack spacing={2}>
    <Stack direction="row" spacing={1}>
      <input
        type="text"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        placeholder="Enter Poshmark username"
        style={{
          padding: '8px 12px',
          borderRadius: '4px',
          border: '1px solid var(--joy-palette-neutral-300)',
          flex: 1
        }}
        onKeyPress={(e) => e.key === 'Enter' && onSearch()}
      />
      <Button
        variant="solid"
        onClick={onSearch}
        loading={isLoading}
      >
        Search
      </Button>
    </Stack>
    {error && (
      <Typography color="danger">{error}</Typography>
    )}
  </Stack>
);

// Log Type Filter Component
const LogTypeFilter = ({ 
  selectedTypes, 
  onTypesChange 
}: {
  selectedTypes: string[];
  onTypesChange: (types: string[]) => void;
}) => {
  const getLogTypeLabel = (type: string) => {
    switch (type) {
      case 'task_complete':
        return 'Task Complete';
      case 'error':
        return 'Error';
      case 'action':
        return 'Action';
      case 'warning':
        return 'Warning';
      case 'info':
        return 'Info';
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Typography level="body-sm" sx={{ minWidth: 'fit-content' }}>
        Filter by type:
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {LOG_TYPES.map(type => (
          <Chip
            key={type}
            variant={selectedTypes.includes(type) ? 'solid' : 'outlined'}
            color={getLogTypeColor(type) as any}
            onClick={() => {
              if (selectedTypes.includes(type)) {
                onTypesChange(selectedTypes.filter(t => t !== type));
              } else {
                onTypesChange([...selectedTypes, type]);
              }
            }}
            sx={{ cursor: 'pointer' }}
          >
            {getLogTypeLabel(type)}
          </Chip>
        ))}
        {selectedTypes.length > 0 && (
          <Button
            variant="plain"
            size="sm"
            onClick={() => onTypesChange([])}
          >
            Clear All
          </Button>
        )}
      </Box>
    </Stack>
  );
};

// Pagination Controls Component
const PaginationControls = ({
  currentPage,
  totalPages,
  startIndex,
  endIndex,
  totalItems,
  onPrevPage,
  onNextPage
}: {
  currentPage: number;
  totalPages: number;
  startIndex: number;
  endIndex: number;
  totalItems: number;
  onPrevPage: () => void;
  onNextPage: () => void;
}) => {
  if (totalPages <= 1) return null;

  return (
    <Stack direction="row" justifyContent="center" spacing={1} alignItems="center">
      <Button
        variant="outlined"
        size="sm"
        onClick={onPrevPage}
        disabled={currentPage === 1}
      >
        Previous
      </Button>
      <Typography level="body-sm">
        Page {currentPage} of {totalPages} • Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems}
      </Typography>
      <Button
        variant="outlined"
        size="sm"
        onClick={onNextPage}
        disabled={currentPage === totalPages}
      >
        Next
      </Button>
    </Stack>
  );
};

// Logs Table Component
const LogsTable = ({ 
  logs, 
  startIndex 
}: {
  logs: Log[];
  startIndex: number;
}) => (
  <Sheet
    sx={{
      overflowX: 'auto',
      // Remove height restriction to allow table to expand to fit all rows
      '& .MuiTable-root': {
        minWidth: '100%'
      }
    }}
  >
    <Table>
      <thead>
        <tr>
          <th style={{ width: '60px' }}>#</th>
          <th style={{ width: '180px' }}>Date</th>
          <th style={{ width: '120px' }}>Type</th>
          <th style={{ width: '120px' }}>Action</th>
          <th>Message</th>
        </tr>
      </thead>
      <tbody>
        {logs.map((log, index) => (
          <tr key={startIndex + index}>
            <td style={{ fontSize: '0.875rem' }}>
              {startIndex + index + 1}
            </td>
            <td style={{ fontSize: '0.875rem' }}>
              {formatDate(log.date)}
            </td>
            <td>
              <Typography
                level="body-sm"
                color={getLogTypeColor(log.type)}
                sx={{ fontWeight: 'bold' }}
              >
                {log.type}
              </Typography>
            </td>
            <td>
              {log.action && (
                <Typography level="body-sm" color="neutral">
                  {log.action}
                </Typography>
              )}
            </td>
            <td style={{
              maxWidth: '400px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
              title={log.message}
            >
              <Typography level="body-sm">
                {log.message}
              </Typography>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  </Sheet>
);

// Main User Search Component
const UserSearchCard = () => {
  // State management
  const [username, setUsername] = React.useState('');
  const [searchResults, setSearchResults] = React.useState<string[]>([]);
  const [userLogs, setUserLogs] = React.useState<Log[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [hasSearched, setHasSearched] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [selectedLogTypes, setSelectedLogTypes] = React.useState<string[]>([]);

  // Search functionality
  const handleSearch = async () => {
    if (!username.trim()) {
      setError('Please enter a username');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setHasSearched(true);
      setCurrentPage(1);
      setSelectedLogTypes([]); // Reset filter on new search

      const client = await createApiClient();

      // Fetch user info
      const userInfoResponse = await client.getUserInfoByUsername(username);
      setSearchResults(userInfoResponse.data);

      // If we found user IDs, fetch logs for the first one
      if (userInfoResponse.data.length > 0) {
        try {
          const logsResponse = await client.getUserLogs(userInfoResponse.data[0]);
          const logs = logsResponse.data.reverse();
          setUserLogs(logs);
        } catch (logsError) {
          console.error('Error fetching user logs:', logsError);
          setUserLogs([]);
        }
      } else {
        setUserLogs([]);
      }
    } catch (err) {
      console.error('Error searching user:', err);
      setError(err instanceof Error ? err.message : 'Failed to search user');
      setSearchResults([]);
      setUserLogs([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter logs based on selected types
  const filteredLogs = React.useMemo(() => {
    if (selectedLogTypes.length === 0) {
      return userLogs;
    }
    return userLogs.filter(log => selectedLogTypes.includes(log.type));
  }, [userLogs, selectedLogTypes]);

  // Pagination calculations with filtered logs
  const totalFilteredLogs = filteredLogs.length;
  const totalPages = Math.ceil(totalFilteredLogs / LOGS_PER_PAGE);
  const startIndex = (currentPage - 1) * LOGS_PER_PAGE;
  const endIndex = startIndex + LOGS_PER_PAGE;
  const paginatedLogs = filteredLogs.slice(startIndex, endIndex);

  // Reset page when filter changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [selectedLogTypes]);

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  return (
    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm' }}>
      <Typography level="title-lg" sx={{ mb: 2 }}>Search User Info</Typography>
      <Stack spacing={3}>
        <UserSearchForm
          username={username}
          setUsername={setUsername}
          onSearch={handleSearch}
          isLoading={isLoading}
          error={error}
        />

        {isLoading ? (
          <Typography><Skeleton>Loading user info...</Skeleton></Typography>
        ) : searchResults.length > 0 ? (
          <Stack spacing={3}>
            {/* Associated Closet IDs */}
            <Sheet variant="soft" sx={{ p: 2 }}>
              <Typography level="title-sm" sx={{ mb: 1 }}>Associated Closet IDs:</Typography>
              <Stack spacing={1}>
                {searchResults.map((closetId, index) => (
                  <Typography key={index}>{closetId}</Typography>
                ))}
              </Stack>
            </Sheet>

            {/* User Logs Section */}
            <Sheet variant="soft" sx={{ p: 2 }}>
              <Stack spacing={2}>
                {/* Header with log count */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography level="title-sm">
                    User Logs ({userLogs?.length ?? 0} total • {totalFilteredLogs} filtered)
                  </Typography>
                </Stack>

                {/* Log Type Filter */}
                {userLogs && userLogs.length > 0 && (
                  <LogTypeFilter
                    selectedTypes={selectedLogTypes}
                    onTypesChange={setSelectedLogTypes}
                  />
                )}

                {/* Pagination Controls - Top */}
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  totalItems={totalFilteredLogs}
                  onPrevPage={handlePrevPage}
                  onNextPage={handleNextPage}
                />

                {/* Logs Table */}
                {filteredLogs.length > 0 ? (
                  <>
                    <LogsTable logs={paginatedLogs} startIndex={startIndex} />
                    {/* Pagination Controls - Bottom */}
                    <PaginationControls
                      currentPage={currentPage}
                      totalPages={totalPages}
                      startIndex={startIndex}
                      endIndex={endIndex}
                      totalItems={totalFilteredLogs}
                      onPrevPage={handlePrevPage}
                      onNextPage={handleNextPage}
                    />
                  </>
                ) : selectedLogTypes.length > 0 ? (
                  <Typography level="body-sm" color="neutral" sx={{ textAlign: 'center', py: 4 }}>
                    No logs found for selected types: {selectedLogTypes.join(', ')}
                  </Typography>
                ) : userLogs.length > 0 ? (
                  <Typography level="body-sm" color="neutral" sx={{ textAlign: 'center', py: 4 }}>
                    No logs found
                  </Typography>
                ) : (
                  <Typography level="body-sm" color="neutral" sx={{ textAlign: 'center', py: 4 }}>
                    No logs found for this user
                  </Typography>
                )}
              </Stack>
            </Sheet>
          </Stack>
        ) : hasSearched && !error ? (
          <Typography>No results found</Typography>
        ) : null}
      </Stack>
    </Sheet>
  );
};

// Custom hook for fetching admin data
const useAdminData = (activeTab: number) => {
  const [schedulesByHour, setSchedulesByHour] = React.useState<Record<string, Array<{ userId: string; closetId: string }>>>({});
  const [usersWithOffersCount, setUsersWithOffersCount] = React.useState<number>(0);
  const [closetMetrics, setClosetMetrics] = React.useState<ClosetMetrics[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);
  const [hasFetchedMetrics, setHasFetchedMetrics] = React.useState(false);

  // Function to fetch metrics manually
  const fetchMetrics = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const client = await createApiClient();
      const metricsResponse = await client.getClosetMetrics();
      setClosetMetrics(metricsResponse.data.metrics || []);
      setHasFetchedMetrics(true);
    } catch (err) {
      console.error('Error fetching metrics:', err);
      setError(err instanceof Error ? err : new Error('Unknown error occurred'));
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const client = await createApiClient();

        switch (activeTab) {
          case 2: // Schedules tab
            const schedulesResponse = await client.getSchedulesByHour();
            setSchedulesByHour(schedulesResponse.data);
            break;
          case 3: // Offers tab
            const offersResponse = await client.getUsersWithOffersCount();
            setUsersWithOffersCount(offersResponse.data.count);
            break;
        }
      } catch (err) {
        console.error('Error fetching admin data:', err);
        setError(err instanceof Error ? err : new Error('Unknown error occurred'));
      } finally {
        setIsLoading(false);
      }
    };

    // Only auto-fetch for non-metrics tabs
    if (activeTab !== 1) {
      fetchData();
    }
  }, [activeTab]); // Add activeTab as dependency

  return {
    schedulesByHour,
    usersWithOffersCount,
    closetMetrics,
    isLoading,
    error,
    fetchMetrics,
    hasFetchedMetrics
  };
};

// Main Admin component
export const Admin = () => {
  const [activeTab, setActiveTab] = React.useState(0);
  const {
    schedulesByHour,
    usersWithOffersCount,
    closetMetrics,
    isLoading,
    error,
    fetchMetrics,
    hasFetchedMetrics
  } = useAdminData(activeTab);

  return (
    <Dashboard>
      <Helmet>
        <title>Admin Dashboard | Poshwatch</title>
        <meta name="description" content="Poshwatch Admin Dashboard " />
        <meta property="og:title" content="Admin Dashboard" />
        <meta property="og:description" content="Poshwatch Admin Dashboard" />
        <meta property="og:url" content="https://poshwatch.io/admin" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={window.location.href} />
      </Helmet>
      <Container>
        <Tabs
          value={activeTab}
          onChange={(event, value) => setActiveTab(value as number)}
          sx={{ bgcolor: 'background.surface' }}
        >
          <TabList>
            <Tab>User Search</Tab>
            <Tab>Metrics</Tab>
            <Tab>Schedules</Tab>
            <Tab>Offers</Tab>
            <Tab>Facebook Pixel</Tab>
          </TabList>
          <TabPanel value={0}>
            <UserSearchCard />
          </TabPanel>
          <TabPanel value={1}>
            {error ? (
              <Typography color="danger">Error loading metrics data. Please try again later.</Typography>
            ) : (
              <ClosetMetricsCard
                metrics={closetMetrics}
                isLoading={isLoading}
                onFetchMetrics={fetchMetrics}
                hasFetched={hasFetchedMetrics}
              />
            )}
          </TabPanel>
          <TabPanel value={2}>
            {error ? (
              <Typography color="danger">Error loading schedules data. Please try again later.</Typography>
            ) : (
              <SchedulesByHourCard schedulesByHour={schedulesByHour || {}} isLoading={isLoading} />
            )}
          </TabPanel>
          <TabPanel value={3}>
            {error ? (
              <Typography color="danger">Error loading offers data. Please try again later.</Typography>
            ) : (
              <UsersWithOffersCard count={usersWithOffersCount} isLoading={isLoading} />
            )}
          </TabPanel>
          <TabPanel value={4}>
            <FacebookPixelEventsCard />
          </TabPanel>
        </Tabs>
      </Container>
    </Dashboard>
  );
};
